// Calendar event — the unification point for everything time-bound on
// the platform (plan.md XIX). The wire shape that GET /calendar/events
// returns. The internal DB schema can carry a few extra columns
// (created_by, updated_at) — this is the SPA-facing projection.
//
// Two flavours of event share this shape:
//   1. SOURCE-DERIVED — auto-materialised by a CalendarEventSource
//      subscriber. Shipped sources currently include articles and
//      scheduling rows (availability / booking); future sources may add
//      surveys, forum Event-RSVP threads, workshops, etc. subjectKind
//      is the source row's LinkableKind and subjectId is the source row
//      id. The calendar updates these in lock-step with the source via
//      the activity stream; direct edits are refused (the route layer
//      rejects PATCH / DELETE on non-custom events).
//   2. CUSTOM — user-created standalone deadlines / reminders.
//      subjectKind = 'custom', subjectId = the event's own id (the
//      event IS its own subject; there's no upstream row to mirror).
//      Editable + deletable by the creator.
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Closed enums
// ---------------------------------------------------------------------------
/** State of a calendar event. The auto-cancel flow (a scheduled
 *  article gets unscheduled by its author) flips status to
 *  `cancelled` rather than hard-deleting the row — keeps a
 *  tombstone for 30 days for audit, per plan.md XIX.6. */
export const CalendarEventStatus = z.enum(['scheduled', 'live', 'past', 'cancelled']);
/** Custom-event sentinel for the polymorphic `subjectKind` slot.
 *  Source-derived events use the owning module's LinkableKind
 *  ('article', 'availability', 'booking', future 'survey', etc.). */
export const CUSTOM_SUBJECT_KIND = 'custom';
// ---------------------------------------------------------------------------
// CalendarEventLink — cross-resource chip
// ---------------------------------------------------------------------------
/** A linked source resource an event references — `{kind, id, label}`.
 *  Rendered as a chip on the event card / detail sheet; the SPA wires
 *  click-through via `onSelectLink`. The trio mirrors the @viquard/calendar
 *  `CalendarEventLink` type (the `icon` field there is a React component
 *  picked client-side from the kind, never on the wire). */
export const CalendarEventLink = z.object({
    /** LinkableKind of the referenced resource — 'article' / 'survey' /
     *  'thread' / 'host' / 'file' / 'member' / future kinds. Free-form
     *  string so consumers can register new kinds without touching the
     *  contract. Length-bounded to keep the wire payload tight. */
    kind: z.string().min(1).max(64),
    /** Stable id within the linked kind. We don't tag this `uuid()`
     *  because some link kinds use slugs / external ids. */
    id: z.string().min(1).max(128),
    /** Display text rendered inside the chip. The SPA can also resolve
     *  a fresh label client-side; the stored value is a snapshot at
     *  attach time so deleted / renamed targets still render something. */
    label: z.string().min(1).max(160),
});
/** Per-event reminder lead time in minutes. `null` on CalendarEvent means
 *  no reminder should be delivered for that event. */
export const CalendarReminderMinutesBefore = z.number().int().min(1).max(10_080);
export const CalendarEventLocationType = z.enum(['online', 'inPerson', 'custom']);
export const CalendarMeetingProvider = z.enum([
    'tencent',
    'zoom',
    'googleMeet',
    'teams',
    'other',
]);
// ---------------------------------------------------------------------------
// CalendarEvent — wire shape
// ---------------------------------------------------------------------------
export const CalendarEvent = z.object({
    id: z.string().uuid(),
    /** Stable key for a generated recurrence instance in list responses.
     *  Null means the object is the persisted row itself. */
    occurrenceId: z.string().min(1).nullable().optional(),
    /** Persisted master event id for generated recurrence instances. */
    recurrenceMasterId: z.string().uuid().nullable().optional(),
    /** Polymorphic subject. Maps to the LinkableKind of the source row
     *  ('article' / 'availability' / 'booking' / future kinds) for
     *  source-derived events; literal
     *  `'custom'` for user-created standalone events. */
    subjectKind: z.string().min(1),
    subjectId: z.string().min(1),
    /** Display title. For source-derived events this is a snapshot at
     *  materialisation time (e.g. "Going live: My Article"). The
     *  source's update path keeps it in sync via re-materialisation. */
    title: z.string().min(1).max(280),
    /** User-facing visual category from the composer's "event type"
     *  picker — `'publish'` / `'newsletter'` / `'survey'` / `'thread'`
     *  / `'workshop'` / `'deadline'` / `'booking'` (or any future themed
     *  kind a consumer registers). Distinct from `subjectKind` — that
     *  one is the polymorphic discriminator the route layer uses for
     *  read-only gating, this one is purely a UI label. Required: every
     *  writer (composer + every CalendarEventSource materialiser) emits
     *  one, so consumers don't have to fall back. */
    kind: z.string().min(1).max(64),
    /** Free-form note text shown beneath the title on event cards /
     *  detail sheets ("12 seats open", "bring drafts", …). 2000-char
     *  cap matches the DB envelope. Nullable; empty strings normalise
     *  to null on write. */
    meta: z.string().min(1).max(2000).nullable(),
    /** Cross-resource references rendered as chips on the event card /
     *  detail sheet. Up to 16 entries — the UI's chip strip would wrap
     *  awkwardly past that and the typical case is 0-2. Nullable;
     *  empty arrays normalise to null on write. */
    links: z.array(CalendarEventLink).max(16).nullable(),
    /** Structured location / online meeting details. Custom events and
     *  source mirrors emit these separately from `meta` so calendar
     *  details and exports can present meeting information without
     *  burying it inside notes. `meetingDetail` may be either a meeting
     *  number or a URL; clients only render open/join affordances when it
     *  is URL-shaped. */
    locationType: CalendarEventLocationType.nullable(),
    meetingProvider: CalendarMeetingProvider.nullable(),
    meetingDetail: z.string().min(1).max(1000).nullable(),
    locationText: z.string().min(1).max(500).nullable(),
    /** ISO 8601 UTC. Always store + compare in UTC; the SPA renders
     *  in the viewer's TZ. */
    startsAt: z.string().datetime({ offset: true }),
    /** Null for point-in-time events (e.g. a publish moment). */
    endsAt: z.string().datetime({ offset: true }).nullable(),
    /** TZ-neutral by spec when true (e.g. all-day deadlines). */
    allDay: z.boolean(),
    /** RFC 5545 RRULE expression; null = single occurrence. */
    recurrence: z.string().nullable(),
    /** Minutes before `startsAt` to send an in-app reminder. `null`
     *  disables reminder delivery for this event. */
    reminderMinutesBefore: CalendarReminderMinutesBefore.nullable(),
    /** Originating timezone (e.g. "America/Los_Angeles"). For
     *  source-derived events, mirrors the source's timezone if it has
     *  one; otherwise 'UTC'. */
    tzid: z.string(),
    /** Visibility scope — drives canRead. Snapshot of the source's
     *  groupIds at materialisation time. Drift caveat: if the source
     *  later changes groups, the calendar row stays at the old set
     *  unless the source emits an update event we re-process. */
    groupIds: z.array(z.string()),
    status: CalendarEventStatus,
    /** Null for system-materialised events; UUID for custom events. */
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=event.js.map